import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import { Avatar } from '../../../../../common/Avatar';
import { Table } from '../../../../../common/Table';
import { Spinner } from '../../../../../components/Spinner';
import { useTeamMembers } from '../../../../../hooks/useTeamMembers';
import { User } from '../../../../../types/index';
import { useTeam } from '../../../contexts/TeamContext';
import { useTimelineData } from '../../../hooks/useTimelineData';
import { TeamMember } from '../../../types/teamMember';
import { getTimeInTeamTimezone } from '../../../utils/timelineUtils';

import { TimelineItem } from './TimelineItems/TimelineItem';

interface TimelineViewProps {
  selectedDate: Date;
  memberId?: string;
  employeeId?: string;
}

export function TimelineView({
  selectedDate,
  memberId,
  employeeId,
}: TimelineViewProps): JSX.Element {
  const { team } = useTeam();
  const { data: teamMembers, isLoading } = useTeamMembers();
  const { data: timelineData, isLoading: isLoadingTimelineData } =
    useTimelineData(
      selectedDate,
      memberId && employeeId
        ? [{ memberId, employeeId }]
        : teamMembers?.map((member) => ({
            employeeId: member.employeeId,
            memberId: member.id,
          })) || [],
    );

  const workHoursStart = team?.work_hours_start || '09:30';
  const checkInTime1 = team?.check_in_times?.[0] || '12:30 PM';
  const checkInTime2 = team?.check_in_times?.[1] || '5:30 PM';
  // Convert times to Date objects in the team's timezone
  const workHoursStartDate = getTimeInTeamTimezone(workHoursStart);
  const checkInTime1Date = getTimeInTeamTimezone(checkInTime1);
  const checkInTime2Date = getTimeInTeamTimezone(checkInTime2);

  const isIndividualView = Boolean(memberId);

  if (isLoading || isLoadingTimelineData) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Spinner />
      </div>
    );
  }

  const filteredMembers = memberId
    ? teamMembers?.filter((member) => member.id === memberId)
    : teamMembers;

  const hasSession3 =
    timelineData &&
    Object.values(timelineData).some(
      (data) => data.sessions.session_3?.items.length > 0,
    );

  const teamMemberColumnWidth = '192px';
  const sessionMinWidth = '150px';
  const fallbackSession3Width = '128px';

  if (hasSession3) {
    const baseWidth = `calc((100% - ${teamMemberColumnWidth}) / 3)`;
    const session3Width = `calc(${baseWidth} * 0.75)`;
    const leftover = `calc(${baseWidth} * 0.25)`;
    const halfLeftover = `calc(${leftover} / 2)`;
    const session1Width = `calc(${baseWidth} + ${halfLeftover})`;
    const session2Width = session1Width;

    const columns = [
      ...(!isIndividualView
        ? [
            {
              key: 'member',
              width: teamMemberColumnWidth,
              title: (
                <div className="h-full flex items-center">Team Member</div>
              ),
              render: (member: TeamMember) => {
                return (
                  team?.id && (
                    <Link
                      to={`/your-teams/${team?.id}/individualView/?memberId=${member.id}`}
                    >
                      <div className="flex items-center gap-3 hover:text-blue-500 ">
                        <Avatar
                          src={member.avatarUrl}
                          name={`${member.firstName} ${member.lastName}`}
                          size="sm"
                        />

                        <span className="text-text-title text-md  font-regular text-nowrap overflow-hidden">
                          {member.firstName}
                        </span>
                      </div>
                    </Link>
                  )
                );
              },
            },
          ]
        : []),
      {
        key: 'session1',
        width: session1Width,
        minWidth: sessionMinWidth,
        borders: { left: true },
        title: (
          <div className="h-full flex items-center relative">
            <span className="absolute left-0 transform">
              {format(workHoursStartDate, 'hh:mm a')} IST
            </span>
          </div>
        ),
        render: (member: TeamMember) => (
          <div className="flex flex-col py-4  px-4 justify-start gap-2">
            {timelineData?.[member.id]?.sessions.session_1.items.map((item) => (
              <TimelineItem
                checkInTime={{
                  checkInTime: checkInTime1,
                  date: selectedDate,
                }}
                isIndividualView={isIndividualView}
                key={item.id}
                item={item}
              />
            ))}
          </div>
        ),
      },
      {
        key: 'session2',
        width: session2Width,
        minWidth: sessionMinWidth,
        borders: { left: true },
        title: (
          <div className="h-full flex items-center relative">
            <span className="absolute left-0 transform">
              {format(checkInTime1Date, 'hh:mm a')} IST
            </span>
          </div>
        ),
        render: (member: TeamMember) => (
          <div className="flex flex-col py-4 px-4 justify-start gap-2">
            {timelineData?.[member.id]?.sessions.session_2.items.map((item) => (
              <TimelineItem
                checkInTime={{
                  checkInTime: checkInTime2,
                  date: selectedDate,
                }}
                isIndividualView={isIndividualView}
                key={item.id}
                item={item}
              />
            ))}
          </div>
        ),
      },
      {
        key: 'session3',
        width: session3Width,
        minWidth: sessionMinWidth,
        borders: { left: true },
        title: (
          <div className="h-full flex items-center relative">
            <span className="absolute left-0 transform">
              {format(checkInTime2Date, 'hh:mm a')} IST
            </span>
          </div>
        ),
        render: (member: TeamMember) => (
          <div className="flex flex-col py-4 space-y-3 px-4 justify-start gap-2">
            {timelineData?.[member.id]?.sessions.session_3.items.map((item) => (
              <TimelineItem
                checkInTime={{
                  checkInTime: checkInTime2,
                  date: selectedDate,
                }}
                isIndividualView={isIndividualView}
                key={item.id}
                item={item}
              />
            ))}
          </div>
        ),
      },
    ];

    return (
      <div className="relative flex flex-col items-start self-stretch p-6 pt-3 bg-[var(--Background-bg-surface-1,#FEFDFC)] border border-[var(--Border-border-gray-light,#F0F0F0)] rounded-lg">
        <div className="w-full">
          <Table<User>
            columns={columns}
            data={filteredMembers || []}
            borderStyle="dashed"
            className="min-h-[200px]"
            containerClassName="overflow-visible"
          />
        </div>
      </div>
    );
  }

  const leftoverWidth = `calc(100% - ${teamMemberColumnWidth} - ${fallbackSession3Width})`;
  const session1Width = `calc(${leftoverWidth} / 2)`;
  const session2Width = `calc(${leftoverWidth} / 2)`;

  const columns = [
    ...(!isIndividualView
      ? [
          {
            key: 'member',
            width: teamMemberColumnWidth,
            title: <div className="h-full flex items-center">Team Member</div>,
            render: (member: TeamMember) =>
              team?.id && (
                <Link
                  to={`/your-teams/${team?.id}/individualView/?memberId=${member.id}`}
                >
                  <div className="flex items-center  gap-3 hover:text-blue-500 ">
                    <Avatar
                      src={member.avatarUrl}
                      name={`${member.firstName} ${member.lastName}`}
                      size="sm"
                    />

                    <span className="text-text-title text-md font-regular text-nowrap overflow-hidden ">
                      {member.firstName}
                    </span>
                  </div>
                </Link>
              ),
          },
        ]
      : []),
    {
      key: 'session1',
      width: session1Width,
      minWidth: sessionMinWidth,
      borders: { left: true },
      title: (
        <div className="h-full flex items-center relative">
          <span className="absolute left-0 transform">
            {format(workHoursStartDate, 'hh:mm a')} IST
          </span>
        </div>
      ),
      render: (member: TeamMember) => (
        <div className="py-4 space-y-3 px-4">
          {timelineData?.[member.id]?.sessions.session_1.items.map((item) => (
            <TimelineItem
              checkInTime={{
                checkInTime: checkInTime1,
                date: selectedDate,
              }}
              isIndividualView={isIndividualView}
              key={item.id}
              item={item}
            />
          ))}
        </div>
      ),
    },
    {
      key: 'session2',
      width: session2Width,
      minWidth: sessionMinWidth,
      borders: { left: true },
      title: (
        <div className="h-full flex items-center relative">
          <span className="absolute left-0 transform">
            {format(checkInTime1Date, 'hh:mm a')} IST
          </span>
        </div>
      ),
      render: (member: TeamMember) => (
        <div className="py-4 space-y-3 px-4">
          {timelineData?.[member.id]?.sessions.session_2.items.map((item) => (
            <TimelineItem
              checkInTime={{
                checkInTime: checkInTime2,
                date: selectedDate,
              }}
              isIndividualView={isIndividualView}
              key={item.id}
              item={item}
            />
          ))}
        </div>
      ),
    },
    {
      key: 'session3',
      width: fallbackSession3Width,
      minWidth: fallbackSession3Width,
      borders: { left: true },
      title: (
        <div className="h-full flex items-center relative">
          <span className="absolute left-0 transform">
            {format(checkInTime2Date, 'hh:mm a')} IST
          </span>
        </div>
      ),
      render: (_member: TeamMember) => (
        <div className="py-4 space-y-3 px-2 justify-start">
          {/* No data, so just empty/placeholder */}
        </div>
      ),
    },
  ];

  return (
    <div className="relative flex flex-col items-start self-stretch p-6 bg-[var(--Background-bg-surface-1,#FEFDFC)] border border-[var(--Border-border-gray-light,#F0F0F0)] rounded-lg">
      <div className="w-full">
        <Table<User>
          columns={columns}
          data={filteredMembers || []}
          borderStyle="dashed"
          className="min-h-[200px]"
          containerClassName="overflow-visible"
        />
      </div>
    </div>
  );
}
